import React from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import btnClose from '../../images/common/close.svg';
import {
  Main,
  CloseButton,
  CloseButtonImage,
  Header,
} from '../../styles/common/Dialog';
import {
  Dialog,
  ContentsBlock,
  BodyBlock,
  SubmitBlock,
  InfoTitle,
  InfoBody,
  InfoBlock,
  InputArea,
  DeviceUsers,
  UserInfo,
  DefaultUse,
  UpdateButton,
} from './styles/UpdateDeviceDialogStyles';

/**
 * デバイス情報更新ダイアログ
 *
 * @param {Object} confirmData - デバイスの詳細情報
 * @param {Object} confirmDataError - デバイスの詳ff細情報のエラー情報
 * @param {Function} onClose - ダイアログを閉じる関数
 * @param {Function} onUpdate - デバイスの詳細情報を更新するための関数
 * @param {Function} onUpdateConfirm - 入力された情報を確認するための関数
 * @param {boolean} displayUpdateButton - 更新ボタンを表示するかどうか
 */
const UpdateDeviceDialog = ({
  confirmData,
  confirmDataError,
  onClose,
  onUpdate,
  onUpdateConfirm,
  displayUpdateButton,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const params = {
    deviceId: confirmData.deviceId,
    deviceNickname: confirmData.deviceNickname,
  };
  const placeholderNickname = formatMessage({
    id: 'device.DeviceDetail.deviceNickname.placeholder',
  });
  const defaultUseMessage = formatMessage({
    id: 'device.DeviceDetail.deviceUserName.defaultUse',
  });
  return (
    <Main
      id="closeUpdateDeviceDialog"
      role="presentation"
      onClick={() => onClose()}
    >
      <Dialog role="presentation" onClick={e => e.stopPropagation()}>
        <ContentsBlock>
          <Header>
            <FormattedMessage id="device.DeviceDetail.title" />
            <CloseButton
              id="closeButton"
              role="presentation"
              className="button"
              onClick={() => onClose()}
            >
              <CloseButtonImage src={btnClose} alt="close" />
            </CloseButton>
          </Header>
          <BodyBlock>
            <InfoBlock>
              <InfoTitle>
                <FormattedMessage id="device.DeviceDetail.title.deviceNickname" />
              </InfoTitle>
              <InfoBody>
                {(displayUpdateButton && (
                  <InputArea
                    isError={confirmDataError.deviceNickname}
                    id="updateDeviceNickname"
                    type="text"
                    defaultValue={confirmData.deviceNickname}
                    onChange={e =>
                      onUpdateConfirm({ deviceNickname: e.target.value })
                    }
                    placeholder={placeholderNickname}
                  />
                )) ||
                  confirmData.deviceNickname}
              </InfoBody>
            </InfoBlock>
            <InfoBlock>
              <InfoTitle>
                <FormattedMessage id="device.DeviceDetail.title.deviceName" />
              </InfoTitle>
              <InfoBody>{confirmData.deviceName}</InfoBody>
            </InfoBlock>
            <InfoBlock>
              <InfoTitle>
                <FormattedMessage id="device.DeviceDetail.title.deviceSerial" />
              </InfoTitle>
              <InfoBody>{confirmData.deviceSerial}</InfoBody>
            </InfoBlock>
            <InfoBlock>
              <InfoTitle>
                <FormattedMessage id="device.DeviceDetail.title.deviceUserName" />
              </InfoTitle>
              <InfoBody>
                <DeviceUsers>
                  {confirmData &&
                    confirmData.deviceUsers &&
                    confirmData.deviceUsers.map(user => {
                      const defaultUse = user.locationInfoOnOff
                        ? defaultUseMessage
                        : '';
                      return (
                        <UserInfo key={`deviceUser${user.operatorId}`}>
                          <div>{user.operatorName}</div>
                          <DefaultUse>{defaultUse}</DefaultUse>
                        </UserInfo>
                      );
                    })}
                </DeviceUsers>
              </InfoBody>
            </InfoBlock>
          </BodyBlock>
          {displayUpdateButton && (
            <SubmitBlock>
              <UpdateButton
                id="updateButtonClick"
                role="presentation"
                className="button"
                onClick={() => {
                  if (!confirmDataError.deviceNickname) {
                    onUpdate(params);
                  }
                }}
              >
                <FormattedMessage id="device.DeviceDetail.button.update" />
              </UpdateButton>
            </SubmitBlock>
          )}
        </ContentsBlock>
      </Dialog>
    </Main>
  );
};

UpdateDeviceDialog.propTypes = {
  confirmData: PropTypes.object.isRequired,
  confirmDataError: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onUpdateConfirm: PropTypes.func.isRequired,
  displayUpdateButton: PropTypes.bool.isRequired,
};

export default UpdateDeviceDialog;
